/**
 * <s-product-media-carousel> — Product detail media carousel with floating thumbnail capsule.
 *
 * Coordinates:
 *   [data-carousel-track]       - Snap-scroll container holding the main media slides.
 *   [data-carousel-slide]       - Individual media slide elements.
 *   [data-carousel-prev]        - Previous slide navigation button.
 *   [data-carousel-next]        - Next slide navigation button.
 *   [data-carousel-thumbnails]  - Container holding thumbnail buttons.
 *   [data-carousel-thumbnail]   - Individual thumbnail button (index-bound).
 *   [data-carousel-current]     - Current slide numeric label.
 */
class SProductMediaCarousel extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbnails = [];
  #prevBtn;
  #nextBtn;
  #currentEl;
  #observer;
  #currentIndex = 0;
  #isScrollingFromClick = false;
  #scrollTimeout;

  connectedCallback() {
    this.#track = this.querySelector('[data-carousel-track]');
    if (!this.#track) return;

    this.#slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.#thumbnails = Array.from(this.querySelectorAll('[data-carousel-thumbnail]'));
    this.#prevBtn = this.querySelector('[data-carousel-prev]');
    this.#nextBtn = this.querySelector('[data-carousel-next]');
    this.#currentEl = this.querySelector('[data-carousel-current]');

    if (this.#slides.length <= 1) {
      if (this.#prevBtn) this.#prevBtn.style.display = 'none';
      if (this.#nextBtn) this.#nextBtn.style.display = 'none';
      const thumbsContainer = this.querySelector('[data-carousel-thumbnails-wrapper]');
      if (thumbsContainer) thumbsContainer.style.display = 'none';
      const counter = this.querySelector('[data-carousel-counter]');
      if (counter) counter.style.display = 'none';
      return;
    }

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    // Attach Prev / Next button listeners
    this.#prevBtn?.addEventListener('click', () => this.#navigate(-1), { signal });
    this.#nextBtn?.addEventListener('click', () => this.#navigate(1), { signal });

    // Attach Thumbnail button listeners
    this.#thumbnails.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => this.goToSlide(idx), { signal });
    });

    // Keyboard navigation
    this.#track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.#navigate(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.#navigate(1);
      }
    }, { signal });

    // Track scroll listener with debounced index resolution
    this.#track.addEventListener('scroll', () => {
      if (this.#isScrollingFromClick) return;
      window.clearTimeout(this.#scrollTimeout);
      this.#scrollTimeout = window.setTimeout(() => this.#resolveCurrentSlide(), 60);
    }, { passive: true, signal });

    // IntersectionObserver for reliable active slide detection
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        if (this.#isScrollingFromClick) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          const index = this.#slides.indexOf(visible[0].target);
          if (index !== -1 && index !== this.#currentIndex) {
            this.#setActiveIndex(index);
          }
        }
      }, {
        root: this.#track,
        threshold: 0.6
      });

      this.#slides.forEach((slide) => this.#observer.observe(slide));
    }

    this.#updateUI();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    window.clearTimeout(this.#scrollTimeout);
  }

  goToSlide(index) {
    if (index < 0 || index >= this.#slides.length) return;
    this.#isScrollingFromClick = true;
    this.#setActiveIndex(index);

    const targetSlide = this.#slides[index];
    if (targetSlide) {
      targetSlide.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }

    window.clearTimeout(this.#scrollTimeout);
    this.#scrollTimeout = window.setTimeout(() => {
      this.#isScrollingFromClick = false;
    }, 450);
  }

  #navigate(direction) {
    let nextIndex = this.#currentIndex + direction;
    if (nextIndex < 0) nextIndex = this.#slides.length - 1;
    if (nextIndex >= this.#slides.length) nextIndex = 0;
    this.goToSlide(nextIndex);
  }

  #resolveCurrentSlide() {
    const trackCenter = this.#track.scrollLeft + (this.#track.offsetWidth / 2);
    let closestIndex = 0;
    let minDistance = Infinity;

    this.#slides.forEach((slide, idx) => {
      const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
      const distance = Math.abs(trackCenter - slideCenter);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = idx;
      }
    });

    if (closestIndex !== this.#currentIndex) {
      this.#setActiveIndex(closestIndex);
    }
  }

  #setActiveIndex(index) {
    this.#currentIndex = index;
    this.#updateUI();
  }

  #updateUI() {
    if (this.#currentEl) {
      this.#currentEl.textContent = String(this.#currentIndex + 1);
    }

    this.#thumbnails.forEach((thumb, idx) => {
      const isActive = idx === this.#currentIndex;
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');

      if (isActive) {
        thumb.classList.add('border-accent', 'ring-2', 'ring-accent/30', 'scale-105', 'opacity-100');
        thumb.classList.remove('border-transparent', 'opacity-60');
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      } else {
        thumb.classList.remove('border-accent', 'ring-2', 'ring-accent/30', 'scale-105', 'opacity-100');
        thumb.classList.add('border-transparent', 'opacity-60');
      }
    });

    // Update prev/next button states
    if (this.#prevBtn) {
      this.#prevBtn.disabled = false;
    }
    if (this.#nextBtn) {
      this.#nextBtn.disabled = false;
    }
  }
}

if (!customElements.get('s-product-media-carousel')) {
  customElements.define('s-product-media-carousel', SProductMediaCarousel);
}
